import type { OmniSearchEntry } from "../types/omni-search";

/**
 * Filters and ranks search entries for a query.
 *
 * The query is trimmed, lowercased, and split on whitespace into tokens. An entry matches when every
 * token appears (as a substring) in its label, its description, or one of its keywords. An empty
 * query matches everything and returns the entries in index order.
 *
 * Matches are ranked in three tiers, and entries keep their index order inside a tier:
 * 1. The label starts with the whole query, tokens joined by single spaces ("prov" or "provider h").
 * 2. Every token appears in the label.
 * 3. Any other match, through the description or a keyword.
 *
 * @param entries - The search index, in index order.
 * @param query - The text the user typed.
 * @returns The matching entries, best matches first.
 */
export function matchSearchEntries(entries: OmniSearchEntry[], query: string): OmniSearchEntry[] {
  const tokens = normalizeText(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [...entries];

  const wholeQuery = tokens.join(" ");
  const tiers: OmniSearchEntry[][] = [[], [], []];
  for (const entry of entries) {
    const tier = rankEntry(entry, tokens, wholeQuery);
    if (tier !== undefined) tiers[tier].push(entry);
  }
  return tiers.flat();
}

/** The tier (0 is best) an entry ranks in, or `undefined` when it doesn't match every token. */
function rankEntry(
  entry: OmniSearchEntry,
  tokens: string[],
  wholeQuery: string,
): number | undefined {
  const label = normalizeText(entry.label);
  const otherText = [entry.description ?? "", ...entry.keywords].map(normalizeText);

  const isEveryTokenMatched = tokens.every(
    (token) => label.includes(token) || otherText.some((text) => text.includes(token)),
  );
  if (!isEveryTokenMatched) return undefined;

  if (label.startsWith(wholeQuery)) return LABEL_PREFIX_TIER;
  const isEveryTokenInLabel = tokens.every((token) => label.includes(token));
  if (isEveryTokenInLabel) return LABEL_TOKENS_TIER;
  return OTHER_MATCH_TIER;
}

/** Lowercases and trims text so matching ignores case and surrounding whitespace. */
function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}

/** The label starts with the whole query. This is the best tier. */
const LABEL_PREFIX_TIER = 0;

/** Every token appears in the label, but the label doesn't start with the whole query. */
const LABEL_TOKENS_TIER = 1;

/** Every token matches, and at least one comes from the description or a keyword. */
const OTHER_MATCH_TIER = 2;
